<script setup lang="ts">
/**
 * Bloc « Livraison et retrait » de la fiche produit — colonne DROITE,
 * immédiatement SOUS le bloc d'achat.
 *
 * POURQUOI EN PREMIER SOUS L'ACHAT : « comment je le reçois, et combien ça me
 * coûte en plus » est la question qui suit immédiatement « combien ça coûte ».
 * Baymard la documente comme première cause d'abandon quand la réponse arrive
 * trop tard. Elle est donc collée au bouton d'achat, en clair, sans clic — plus
 * derrière un onglet qu'il fallait penser à ouvrir.
 *
 * Les tarifs viennent des helpers canoniques de `shopPricing.ts`, les MÊMES
 * qu'au checkout : un tarif annoncé ici est celui qui sera facturé.
 *
 * ⚠️ Aucun mode configuré par l'association → le bloc entier disparaît. On
 * n'invente pas une promesse de livraison que la boutique ne tient pas.
 *
 * CONDITIONS DE VENTE : simple lien discret en pied de bloc plutôt qu'un volet
 * dédié. Les CGV doivent être ACCESSIBLES (elles seront de toute façon à
 * accepter à la commande), pas mises en avant au même rang qu'un argument de
 * vente. Si l'association n'a pas de mode de livraison mais a des CGV, le lien
 * est rendu seul dans sa propre carte — il ne disparaît jamais silencieusement.
 */
import type { ShopShippingMethod } from "~/types/shop";
import {
  shopShippingDelayLabel,
  shopShippingFreeAboveHint,
  shopShippingPriceLabel,
} from "~/utils/shopPricing";

const { t } = useI18n();

const props = defineProps<{
  /** Modes de livraison/retrait actifs de la boutique. */
  shippingMethods: ShopShippingMethod[];
  /** URL publique des CGV de la boutique (null si non fournies). */
  termsUrl: string | null;
}>();

/**
 * Tarifs affichés hors panier : on raisonne à panier vide (0 €), donc un mode
 * au franco non atteint montre son prix plein + « Offert dès X € ». C'est la
 * lecture honnête sur une fiche produit, où le panier n'est pas encore connu.
 */
const CATALOG_BASKET_AMOUNT = 0;

const deliveryMethods = computed(() =>
  props.shippingMethods.filter((m) => m.type === "delivery"),
);
const pickupMethods = computed(() =>
  props.shippingMethods.filter((m) => m.type === "pickup"),
);

const hasShipping = computed(() => props.shippingMethods.length > 0);

/** Ni mode de livraison ni CGV → le parent n'affiche pas de carte vide. */
const hasContent = computed(() => hasShipping.value || !!props.termsUrl);

/** Titre honnête : « Retrait » seul si l'asso ne propose aucune livraison. */
const shippingTitle = computed(() => {
  if (deliveryMethods.value.length === 0) return t("boutique.productShipping.titreRetrait");
  if (pickupMethods.value.length === 0) return t("boutique.productShipping.titreLivraison");
  return t("boutique.productShipping.titreLivraisonEtRetrait");
});

function priceLabel(method: ShopShippingMethod): string {
  return shopShippingPriceLabel(method, CATALOG_BASKET_AMOUNT);
}
function freeAboveHint(method: ShopShippingMethod): string | null {
  return shopShippingFreeAboveHint(method, CATALOG_BASKET_AMOUNT);
}
function delayLabel(method: ShopShippingMethod): string | null {
  return shopShippingDelayLabel(method);
}
</script>

<template>
  <section
    v-if="hasContent"
    :aria-labelledby="hasShipping ? 'boutique-shipping-title' : undefined"
    :aria-label="hasShipping ? undefined : $t('boutique.productShipping.ariaConditionsVente')"
    class="bg-white dark:bg-[#141414] rounded-3xl shadow-[0_1px_3px_rgba(45,46,92,0.12)] p-4 md:p-6"
  >
    <template v-if="hasShipping">
      <h2
        id="boutique-shipping-title"
        class="text-sm font-bold uppercase tracking-[0.12em] text-[#2d2e5c] dark:text-white"
      >
        {{ shippingTitle }}
      </h2>

      <ul class="space-y-3 mt-4">
        <li
          v-for="method in shippingMethods"
          :key="method.id"
          class="rounded-2xl border border-gray-100 dark:border-[#262626] p-3"
        >
          <div class="flex items-start justify-between gap-3">
            <span class="flex items-center gap-2 min-w-0">
              <UIcon
                :name="method.type === 'pickup' ? 'i-lucide-map-pin' : 'i-lucide-package'"
                class="size-4 shrink-0 text-[#717198] dark:text-gray-400 mt-0.5"
              />
              <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white">
                {{ method.name }}
              </span>
            </span>
            <span class="text-sm font-semibold text-[#2d2e5c] dark:text-white whitespace-nowrap tabular-nums">
              {{ priceLabel(method) }}
            </span>
          </div>

          <p v-if="method.description" class="text-xs text-[#717198] dark:text-gray-500 mt-1">
            {{ method.description }}
          </p>
          <p
            v-if="freeAboveHint(method)"
            class="text-xs font-medium text-emerald-600 dark:text-emerald-400 mt-1"
          >
            {{ freeAboveHint(method) }}
          </p>
          <p v-if="delayLabel(method)" class="text-xs text-[#717198] dark:text-gray-500 mt-1">
            {{ $t("boutique.productShipping.sousDelai", { delai: delayLabel(method) }) }}
          </p>

          <!-- Retrait : l'acheteur doit savoir OÙ venir avant de commander. -->
          <template v-if="method.type === 'pickup'">
            <p
              v-if="method.pickup_address"
              class="text-xs text-[#505870] dark:text-gray-400 mt-1.5 whitespace-pre-line"
            >
              {{ method.pickup_address }}
            </p>
            <p
              v-if="method.pickup_instructions"
              class="text-xs text-[#717198] dark:text-gray-500 mt-0.5 whitespace-pre-line"
            >
              {{ method.pickup_instructions }}
            </p>
          </template>
        </li>
      </ul>

      <p class="text-[11px] text-[#717198] dark:text-gray-500 mt-3">
        {{ $t("boutique.productShipping.modeDefinitif") }}
      </p>
    </template>

    <!-- CGV : lien sobre, jamais un volet à part entière. -->
    <a
      v-if="termsUrl"
      :href="termsUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="inline-flex items-center gap-1.5 text-xs font-medium text-[#717198] dark:text-gray-400 hover:text-[#2d2e5c] dark:hover:text-white transition-colors underline underline-offset-2"
      :class="hasShipping ? 'mt-2' : ''"
    >
      <UIcon name="i-lucide-file-text" class="size-3.5 shrink-0" />
      {{ $t("boutique.productShipping.cgv") }}
    </a>
  </section>
</template>
